import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { PageHero, Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import Faq from "@/components/Faq";
import { BrowIllu, LipsIllu, WaveMark } from "@/components/Illustrations";
import { CAMINO_NAGI, waKarina } from "@/lib/site";

const NagiBlock = ({ id, num, title, quote, ideal, noRecomendado, faqs, cuidados, Illu, photo, dark }) => (
  <section className={`${dark ? "bg-ink text-cream" : "bg-cream text-ink"} px-6 sm:px-12 lg:px-20 py-20 lg:py-32`} data-testid={`nagi-${id}`}>
    <div className="grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <SectionHead dark={dark} num={num} eyebrow={`NAGI ${id === "brows" ? "Brows" : "Lips"}`} title={title} />
        <Reveal delay={0.15}>
          <p className={`mt-6 leading-relaxed ${dark ? "text-cream/70" : "text-ink/70"}`}>{quote}</p>
          <div className="relative inline-block mt-10">
            <div className="absolute -inset-3 border border-gold/40 pointer-events-none" />
            <img
              src={photo}
              alt={`NAGI ${id === "brows" ? "Brows" : "Lips"} — resultado real`}
              data-testid={`nagi-${id}-photo`}
              className="max-h-[380px] lg:max-h-[440px] w-auto object-contain"
            />
          </div>
          <Illu className="w-24 mt-8 text-gold/60" />
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7 space-y-12">
        <Reveal>
          <h3 className="font-serif text-2xl mb-4 italic text-gold">¿Para quién es ideal?</h3>
          <p className={`text-sm leading-relaxed ${dark ? "text-cream/70" : "text-ink/70"}`}>{ideal}</p>
        </Reveal>
        <Reveal>
          <h3 className="font-serif text-2xl mb-4 italic text-gold">No recomendado para</h3>
          <p className={`text-sm leading-relaxed ${dark ? "text-cream/70" : "text-ink/70"}`}>{noRecomendado}</p>
        </Reveal>
        <Reveal>
          <h3 className="font-serif text-2xl mb-6 italic text-gold">Preguntas frecuentes</h3>
          <Faq items={faqs} dark={dark} testid={`faq-${id}`} />
        </Reveal>
        <Reveal>
          <h3 className="font-serif text-2xl mb-4 italic text-gold">Cuidados posteriores</h3>
          <p className={`text-sm leading-relaxed ${dark ? "text-cream/70" : "text-ink/70"}`}>{cuidados}</p>
        </Reveal>
        <Reveal>
          <a
            href={waKarina(id === "brows" ? "NAGI Brows" : "NAGI Lips")}
            target="_blank"
            rel="noreferrer"
            data-testid={`nagi-${id}-cta`}
            className={`inline-flex items-center gap-3 rounded-full px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
              dark ? "bg-gold text-ink hover:bg-cream" : "bg-ink text-cream hover:bg-gold hover:text-ink"
            }`}
          >
            Reservar {id === "brows" ? "NAGI Brows" : "NAGI Lips"} <ArrowUpRight size={14} />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

const GALLERY = [
  { id: 0, title: "NAGI Brows · Resultado real", src: "/images/nagi-brows.jpg" },
  { id: 1, title: "NAGI Brows · Antes", src: "/images/nagi-brows-resultado-2.jpg" },
  { id: 2, title: "NAGI Brows · Después", src: "/images/nagi-brows-resultado-3.jpg" },
  { id: 3, title: "NAGI Brows · Antes", src: "/images/nagi-brows-antes-2.jpg" },
  { id: 4, title: "NAGI Brows · Después", src: "/images/nagi-brows-despues-2.jpg" },
  { id: 5, title: "NAGI Brows · Antes", src: "/images/nagi-brows-antes-3.jpg" },
  { id: 6, title: "NAGI Brows · Después", src: "/images/nagi-brows-despues-3.jpg" },
  { id: 7, title: "NAGI Brows · El espejo no miente", src: "/images/nagi-brows-espejo.jpg" },
  { id: 8, title: "NAGI Brows · Clienta feliz", src: "/images/nagi-brows-clienta.webp" },
  { id: 9, title: "NAGI Brows · Edición arte", src: "/images/nagi-brows-arte.jpg" },
  { id: 10, title: "NAGI Lips · Resultado real", src: "/images/nagi-lips.webp" },
  { id: 11, title: "NAGI Lips · Primer plano", src: "/images/nagi-lips-2.webp" },
  { id: 12, title: "NAGI Lips · Antes y después", src: "/images/nagi-lips-antes-despues.webp" },
];

const Gallery = () => {
  const [index, setIndex] = useState(0);
  const prev = () => setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length);
  const next = () => setIndex((i) => (i + 1) % GALLERY.length);

  return (
    <section className="bg-ink text-cream px-6 sm:px-12 lg:px-20 py-20 lg:py-28 border-t border-gold/15" data-testid="nagi-galeria">
      <SectionHead dark num="04" eyebrow="Trabajos realizados" title={<>Resultados en <span className="italic text-gold">primer plano</span></>} />
      <Reveal className="mt-6 max-w-xl">
        <p className="text-sm text-cream/60 leading-relaxed">
          Resultados reales de la Técnica NAGI — esta galería irá creciendo con nuevos trabajos en formato
          macro, antes y después.
        </p>
      </Reveal>

      <div className="relative mt-12 border border-gold/20 bg-obsidian">
        <div className="relative h-[58vh] lg:h-[72vh] flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col items-center gap-6 px-8"
            >
              <img
                src={GALLERY[index].src}
                alt={GALLERY[index].title}
                data-testid="gallery-image"
                className="max-h-[46vh] lg:max-h-[60vh] w-auto object-contain"
              />
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cream/50" data-testid="gallery-slide-title">
                {GALLERY[index].title}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
        <button
          onClick={prev}
          data-testid="gallery-prev-button"
          aria-label="Anterior"
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
        >
          <ArrowLeft size={17} />
        </button>
        <button
          onClick={next}
          data-testid="gallery-next-button"
          aria-label="Siguiente"
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
        >
          <ArrowRight size={17} />
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6 pr-14 sm:pr-0">
        <div className="flex gap-2">
          {GALLERY.map((g) => (
            <button
              key={g.id}
              onClick={() => setIndex(g.id)}
              data-testid={`gallery-dot-${g.id}`}
              aria-label={`Ir a slide ${g.id + 1}`}
              className={`h-1 transition-all duration-500 ${index === g.id ? "w-10 bg-gold" : "w-4 bg-cream/25"}`}
            />
          ))}
        </div>
        <p className="font-serif italic text-cream/60 text-lg" data-testid="gallery-ig-quote">
          “La mejor !!” — <span className="text-gold not-italic font-mono text-xs tracking-[0.15em]">@lucia.am3</span>
        </p>
      </div>

      <Reveal className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <p className="font-serif text-2xl text-cream/85">¿Te imaginas tu resultado aquí?</p>
        <a
          href={waKarina("una valoración gratuita (vengo de la galería NAGI)")}
          target="_blank"
          rel="noreferrer"
          data-testid="gallery-reserva-cta"
          className="inline-flex items-center gap-3 rounded-full bg-gold text-ink px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] hover:bg-cream transition-colors duration-300"
        >
          Reserva tu valoración <ArrowUpRight size={14} />
        </a>
      </Reveal>
    </section>
  );
};

const TecnicaNagi = () => (
  <div data-testid="tecnica-nagi-page">
    <PageHero
      dark
      eyebrow="Técnica NAGI"
      lines={["“El momento exacto en que el viento se detiene", "y el mar queda en calma.”"]}
      sub="Micropigmentación de cejas y labios con resultados naturales y comprobados — sin efecto tatuaje, diseñada para pieles sensibles al sol y climas exigentes."
    />

    <section className="bg-beige px-6 sm:px-12 lg:px-20 py-16 lg:py-24 border-y border-sand" data-testid="camino-nagi">
      <Eyebrow>El Camino NAGI — cada resultado sigue un mismo proceso</Eyebrow>
      <div className="mt-10 flex flex-wrap items-center gap-y-8">
        {CAMINO_NAGI.map((paso, i) => (
          <Reveal key={paso} delay={i * 0.08} className="flex items-center">
            <div className="flex flex-col" data-testid={`camino-step-${i + 1}`}>
              <span className="font-mono text-[10px] text-gold tracking-[0.25em] mb-2">0{i + 1}</span>
              <span className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ink">{paso}</span>
            </div>
            {i < CAMINO_NAGI.length - 1 && <span className="mx-5 sm:mx-8 h-px w-8 sm:w-14 bg-gold/50 mt-6" />}
          </Reveal>
        ))}
      </div>
    </section>

    <NagiBlock
      id="brows"
      num="02"
      title={<>Cejas que despiertan <span className="italic text-gold">contigo</span></>}
      quote="Trabaja de forma superficial, por lo que el pigmento se desvanece con el tiempo de manera natural. Está adaptada específicamente para pieles sensibles al sol y climas exigentes, con un objetivo claro: nada de efecto tatuaje."
      ideal="Para quienes sueñan con despertar ya con las cejas hechas — definidas, con la forma perfecta para tu rostro, sin depender del lápiz o el gel cada mañana. Ideal también si has perdido vello en las cejas, o si buscas que se vean más uniformes y definidas."
      noRecomendado="Embarazadas o en lactancia, personas alérgicas a pigmentos, piel con acné activo en la zona de cejas, diabéticas sin control médico."
      faqs={[
        ["¿Duele?", "No, el procedimiento es indoloro."],
        ["¿Cuánto dura la cita?", "Varía según cada caso — no apuramos el proceso por cumplir un horario."],
        ["¿Cuánto tarda en sanar?", "Entre 15 y 21 días."],
        ["¿Cada cuánto se retoca?", "Cada 12 a 18 meses, según cada piel."],
      ]}
      cuidados="Las primeras 24h, cejas secas. Después, solo agua y jabón neutro por 3 días, luego la crema indicada de 7 a 14 días. Evita sol directo (15 días), piscina/playa (10 días), ejercicio (7 días), sauna y maquillaje en la zona mientras cicatriza."
      Illu={BrowIllu}
      photo="/images/nagi-brows.jpg"
      dark={false}
    />

    <NagiBlock
      id="lips"
      num="03"
      title={<>Labios con color y <span className="italic text-gold">vida propia</span></>}
      quote="Aporta color y definición de forma natural, sin efecto pintado ni contorno artificial. Un resultado pensado para adaptarse a ti — a la forma, el tono y las necesidades de tus labios."
      ideal="Para quienes quieren labios con color y vida propia, sin depender del labial — ideal si sientes que se ven pálidos, poco definidos, o simplemente quieres realzarlos de forma natural. También para quienes tienen labios oscuros y desean mejorar su apariencia y color."
      noRecomendado="Embarazadas o en lactancia, personas alérgicas a pigmentos, personas con herpes labial activo o brote reciente (se recomienda tratamiento preventivo antes del procedimiento), diabéticas sin control médico."
      faqs={[
        ["¿Duele?", "Puede sentirse una sensibilidad leve, perfectamente tolerable."],
        ["¿Cuánto dura la cita?", "Varía según cada caso — no apuramos el proceso por cumplir un horario."],
        ["¿Cuánto tarda en sanar?", "Entre 15 y 21 días; el color final se asienta por completo alrededor del día 45."],
        ["¿Cada cuánto se retoca?", "Cada 12 a 18 meses, según cada piel."],
      ]}
      cuidados="Manos limpias antes de tocarlos, sin arrancar costras, con la crema cicatrizante indicada. Evita sol, piscina, mar y fumar (7 días); bebidas/comidas calientes (24h); alcohol y colorantes. Si eres propensa al herpes, sigue el tratamiento preventivo indicado por tu médico. Es normal que el color se oscurezca al inicio y luego aclare hasta el día 20 — es parte del proceso."
      Illu={LipsIllu}
      photo="/images/nagi-lips.webp"
      dark
    />

    <Gallery />

    <section className="bg-cream px-6 sm:px-12 lg:px-20 py-20 lg:py-28" data-testid="nagi-cierre">
      <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl">
        <Reveal>
          <WaveMark className="w-32 text-gold" />
          <p className="font-serif italic text-2xl sm:text-3xl mt-8 leading-snug">
            “Calma perfecta: así se ve un resultado cuando la técnica y el criterio se encuentran.”
          </p>
        </Reveal>
        <Reveal delay={0.15} className="justify-self-center lg:justify-self-end">
          <div className="relative">
            <div className="absolute -inset-3 border border-gold/40 pointer-events-none" />
            <img
              src="/images/karina-creadora.jpg"
              alt="Karina Méndez, creadora de la Técnica NAGI"
              data-testid="nagi-creadora-image"
              className="max-h-[420px] lg:max-h-[480px] w-auto object-contain"
            />
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default TecnicaNagi;
